import React, { useState, useEffect } from "react";
import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Fade from "@mui/material/Fade";
import Typography from "@mui/material/Typography";
import CloseIcon from "@mui/icons-material/Close";
import { Button, Grid, Avatar, RadioGroup, FormControlLabel, Radio } from "@mui/material";
import { API_URL } from "../../consts";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import FormHelperText from "@mui/material/FormHelperText";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import { popupTimer } from "../../consts";
import space1 from "../../Assets/space-1.svg";
import RatingObject from "../RatingObject/RatingObject";
import { setSpaceAndObject, clearSpaceAndObject } from "../../Redux/reducer";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 400,
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: 5,
  p: 4,
};

const AddToSpace = ({ open, handleClose, handleComplete, authorized, rateId, objectId, itemData, currentSpaceId, onRefreshRates, preselectedSpaceId }) => {
  // Clarify the different IDs:
  // - actualObjectId: The object ID for creating new rating (itemData.object_id) - used by RatingObject POST
  // - ratingId: The existing rating ID for removing from space (itemData.id) - used by remove-item API
  const ratingId = itemData?.id || rateId; // Use id as the rating ID for remove-item
  // IMPORTANT: Always use object_id, never id or _id, especially when on spaces page
  const actualObjectId = itemData?.object_id || objectId; // Use object_id as the object ID for RatingObject POST
  
  // Log warning if object_id is missing
  if (!actualObjectId && itemData) {
    console.warn('WARNING: object_id is missing from itemData. This may cause issues on spaces page.', itemData);
  }
  const dispatch = useDispatch();
  const { selectedSpaceId, selectedObjectId } = useSelector(state => ({
    selectedSpaceId: state.selectedSpaceId,
    selectedObjectId: state.selectedObjectId
  }));

  const [form, setForm] = useState({
    space: preselectedSpaceId || "",
  });
  const [submit, setSubmit] = useState(false);
  const [spaces, setSpaces] = useState([]);
  const [showRatingObject, setShowRatingObject] = useState(false);
  const [actionType, setActionType] = useState("duplicate"); // "move" or "duplicate"
  const [preservedItemData, setPreservedItemData] = useState(itemData);

  useEffect(() => {
    
    // Only update preserved data if we don't have any yet and new data comes in
    if (itemData && !preservedItemData) {
      setPreservedItemData(itemData);
    }
  }, [itemData]); // Only depend on itemData

  const handleFormChange = (e) => {
    e.stopPropagation();
    setSubmit(false);
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  useEffect(() => {
    if (authorized) {
      getSpaces();
    }
  }, []);

  // Update form when preselectedSpaceId changes
  useEffect(() => {
    if (preselectedSpaceId) {
      setForm(prev => ({ ...prev, space: preselectedSpaceId }));
    }
  }, [preselectedSpaceId]);

  const getSpaces = () => {
    let url = API_URL + `/spaces?minimal_mode=true`;

    axios
      .get(url)
      .then((res) => {
        setSpaces(res.data.spaces);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({
            type: "SET_UNAUTHORIZED",
          });
          return;
        }
      });
  };

  const handleFormSubmit = () => {
    setSubmit(true);
    if (form.space) {
      dispatch({
        type: "SET_SPACE_AND_OBJECT",
        payload: {
          spaceId: form.space,
          objectId: actualObjectId
        }
      });
      setShowRatingObject(true);
    }
  };

  const handleRatingSubmit = (ratingData) => {
    
    
    if (!selectedSpaceId || !selectedObjectId) {
      console.error('Missing space ID or object ID');
      return;
    }
    
    let url = API_URL + `/rates/authenticated`;
    
    let data = {
      metadata: {
        page_id: { href: selectedObjectId },
        page_url: ratingData.linkUrl || "",
        css_path: "",
      },
      title: ratingData.title,
      comment: ratingData.comment,
      rate: {
        rate_points: ratingData.rate_points,
        tags: ratingData.tags,
        spaces: [selectedSpaceId]
      },
      image: ratingData.image || "",
      rated_object_id: selectedObjectId,
      space_id: selectedSpaceId
    };


    axios
      .post(url, data, {
        headers: {
          "Content-Type": "application/json",
        },
      })
      .then((res) => {
          // If action is "move", remove from current space
          if (actionType === "move" && currentSpaceId && ratingId) {
            const removeUrl = `${API_URL}/spaces/${currentSpaceId}/remove-item`;
            axios
              .post(removeUrl, { rate_id: ratingId })
              .then(() => {
              })
              .catch((removeErr) => {
                console.error('Error removing from original space:', removeErr);
                // Don't show error to user as the main action succeeded
              });
          }

        setForm({
          space: ""
        });
        setActionType("duplicate");
        setShowRatingObject(false);
        setPreservedItemData(null);
        dispatch({
          type: "CLEAR_SPACE_AND_OBJECT"
        });
        handleComplete(); // Use handleComplete instead of handleClose
        dispatch({
          type: "SET_POPUP",
          payload: {
            text: actionType === "move" ? "Item Moved Successfully" : "Item Added to Collection Successfully",
            type: "success",
          },
        });
        setTimeout(() => {
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: "",
              type: "",
            },
          });
        }, popupTimer);
      })
      .catch((err) => {
        if (err?.response?.status === 401) {
          dispatch({
            type: "SET_UNAUTHORIZED",
          });
          return;
        }
      });
  };

  return (
    <>
      <Modal
        aria-labelledby="transition-modal-title"
        aria-describedby="transition-modal-description"
        open={open}
        closeAfterTransition
      >
        <Fade in={open}>
          <Box sx={style} className="signup-modal">
            <Typography id="transition-modal-title" variant="h6" component="h2">
              <CloseIcon
                className="cross-icon"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleComplete(); // Use handleComplete to fully close
                  setSubmit(false);
                  setForm({
                    space: ""
                  });
                  setActionType("duplicate");
                  setPreservedItemData(null);
                  dispatch({
                    type: "CLEAR_SPACE_AND_OBJECT"
                  });
                }}
              />
              <div className="card-heading mb-81"></div>
            </Typography>
            <Box id="transition-modal-description">
              <Typography
                id="transition-modal-title"
                variant="h2"
                component="h2"
                sx={{ fontSize: "20px", textAlign: "center", fontWeight: "700", mb: 2 }}
              >
                Add Item to Collection
              </Typography>

              {/* Action Type Selection */}
              <FormControl component="fieldset" sx={{ m: 1, mb: 2 }} fullWidth>
                <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 600 }}>
                  Choose Action:
                </Typography>
                <RadioGroup
                  value={actionType}
                  onChange={(e) => setActionType(e.target.value)}
                  row
                >
                  <FormControlLabel 
                    value="duplicate" 
                    control={<Radio />} 
                    label="Duplicate (Keep in current collection)" 
                  />
                  <FormControlLabel 
                    value="move" 
                    control={<Radio />} 
                    label="Move (Remove from current collection)" 
                  />
                </RadioGroup>
              </FormControl>

              <FormControl variant="standard" sx={{ m: 1 }} fullWidth>
                <InputLabel id="demo-simple-select-standard-label">
                  Target Collection
                </InputLabel>
                <Select
                  labelId="demo-simple-select-standard-label"
                  id="demo-simple-select-standard"
                  value={form.space}
                  className="customSelect"
                  name="space"
                  error={submit && form.space === "" ? true : false}
                  onChange={handleFormChange}
                  label="Space"
                >
                  {spaces.map((space, index) => (
                    <MenuItem key={space.id} value={space.id}>
                      <Avatar
                        src={space1}
                        sx={{ width: "40px", height: "40px", mr: 2 }}
                      />
                      {space.name}
                    </MenuItem>
                  ))}
                </Select>
                {submit && form.space === "" ? (
                  <FormHelperText sx={{color:"#d32f2f"}}>Field is Required</FormHelperText>
                ) : null}
              </FormControl>

              <Grid container sx={{ margin: "0 auto" }}>
                <Grid item sx={{ margin: "0 auto" }}>
                  <Button
                    className="connectButton"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleFormSubmit(e);
                    }}
                    variant="contained"
                  >
                    {actionType === "move" ? "Move Item" : "Duplicate Item"}
                  </Button>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Fade>
      </Modal>

      <RatingObject
        open={showRatingObject}
        handleClose={() => {
          setShowRatingObject(false);
          // Don't clear Redux state here - only close the RatingObject dialog
          // The state will be cleared when the entire AddToSpace process is completed or cancelled
        }}
        id={actualObjectId}
        rateId={ratingId}
        initialData={preservedItemData || itemData}
        preselectedSpaceId={form.space}
        onRateSuccessfully={(submittedRateId) => {
          // If action is "move", remove from current space
          if (actionType === "move" && currentSpaceId && ratingId) {
            const removeUrl = `${API_URL}/spaces/${currentSpaceId}/remove-item`;
            axios
              .post(removeUrl, { rate_id: ratingId })
              .then(() => {
              })
              .catch((removeErr) => {
                console.error('Error removing from original space:', removeErr);
                // Don't show error to user as the main action succeeded
              });
          }
          
          setForm({
            space: ""
          });
          setActionType("duplicate");
          setShowRatingObject(false);
          setPreservedItemData(null);
          dispatch({
            type: "CLEAR_SPACE_AND_OBJECT"
          });
          handleComplete(); // Use handleComplete instead of handleClose
          
          // Refresh rates if callback provided
          if (onRefreshRates) {
            onRefreshRates();
          }
          
          dispatch({
            type: "SET_POPUP",
            payload: {
              text: actionType === "move" ? "Item Moved Successfully" : "Item Added to Collection Successfully",
              type: "success",
            },
          });
          setTimeout(() => {
            dispatch({
              type: "SET_POPUP",
              payload: {
                text: "",
                type: "",
              },
            });
          }, popupTimer);
        }}
      />
    </>
  );
};

export default AddToSpace;
